import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import cmsPageQuery from 'queries/cmsPage.graphql';
import { cacheControl } from 'constants/cache-control';
import { Container, Box } from 'components/box';
import { Heading } from 'components/typography';
import RichText from 'components/rich-text';
import { QueryCmsPageOutput, CmsPageInterface, PageProps } from 'types/types';
import config from '../../util/load-config';
import { prepMetaTags } from '../../util/seo';
import { canonicalUrl } from '../../util/url';
import { logError } from '../../util/error-logger';
import { logInvalidJSON } from '../../util/log-invalid-json';
import { fetchGraphQl, parseQueryAST } from '../../util/graphql';
import { styledCms } from 'shared/style';
import { Link } from 'components/configurable-routing';
import BackIcon from 'shared/assets/icons/back-caret.svg';
import { css } from '@emotion/react';
import RelatedProducts from 'components/_pages/products/related-products';

export interface CmsPageProps {
  page: CmsPageInterface;
  highlightedBackground?: boolean;
}

const BrandPage = ({ page }: CmsPageProps) => (
  <Container>
    <Link href="/brands" title="Back to Brands" fontWeight={500}>
      <Box
        display="flex"
        alignItems="center"
        css={css`
          gap: 8px;
        `}
      >
        <BackIcon height={15} style={{ display: 'block' }} />
        Back to Brands
      </Box>
    </Link>

    <Box>
      <Heading as="h2" color="black" marginBottom={3} lineHeight={1.5}>
        {page.contentHeading}
      </Heading>

      <RichText
        css={theme => styledCms(theme, { highlightedBackground: false })}
        content={page.content}
        unstyled
      />
      {/*TODO: Uncomment when UNBXD is implemented - https://onedayonly.atlassian.net/browse/FE-1260*/}
      {/* <RelatedProducts customTitle="Products you may like" hideRelated /> */}
    </Box>
  </Container>
);

export async function getServerSideProps({
  query: { id },
  res,
  resolvedUrl,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<CmsPageProps & PageProps>
> {
  const canonical = canonicalUrl(resolvedUrl);

  try {
    const [query, operationName] = await parseQueryAST(cmsPageQuery, 'brands');

    const { data, errors } = await fetchGraphQl<QueryCmsPageOutput>({
      operationName,
      query,
      variables: { identifier: id },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.page) {
      // log error
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(firstError.message, `brand-page.query.${id}`);
      }

      // returns the default 404 page with a status code of 404
      return { notFound: true };
    }

    // go ahead
    res.setHeader('cache-control', cacheControl.dynamic);

    const meta = config.seo.meta.pages[`brands/${id}`];

    return {
      props: {
        ...(canonical ? { layout: { metaTags: { canonical } } } : {}),
        ...(meta ? { layout: { metaTags: prepMetaTags(meta) } } : {}),
        page: data.page,
      },
    };
  } catch (e) {
    logError(e, `brand-page.catch.${id}`);

    // returns the default 404 page with a status code of 404
    return { notFound: true };
  }
}

export default BrandPage;
